/**
 * The Peppol participant-identifier prefixes (EAS / ISO 6523 ICD codes) the lookup page offers.
 * A curated slice of the official list, ordered for this bridge's market: the Slovak and Czech
 * schemes first, then the neighbours, then the pan-European ones. The directory accepts any code,
 * so a prefix missing here can still be typed as a full `scheme:id`.
 */
export interface ParticipantScheme {
  /** The numeric prefix, e.g. `0245`. */
  code: string;
  /**
   * ISO country whose national identifier this scheme carries — what a pasted `CZ12345678` is
   * matched on. Absent for country-less schemes (GLN, DUNS) and for those where a two-letter
   * prefix would be a coincidence rather than a country (Danish CVR). A country-prefixed paste
   * always keeps its letters: the VAT schemes store the whole VAT number (`9950:SK2120147656`),
   * and a scheme holding the bare national id (Slovak `0245` DIČ) is only ever chosen by hand or
   * as the default for a bare number.
   */
  country?: string;
  /** Wins when several schemes share a country, e.g. Slovakia's 9950 (IČ DPH) over 0245 (DIČ). */
  preferred?: boolean;
  /** Shown under the prefix in the dropdown; also documents the expected shape. */
  example: string;
  /** Translation key for the human name; the values live in `peppol.schemes` in the locales. */
  labelKey:
    | 'peppol.schemes.0245'
    | 'peppol.schemes.9950'
    | 'peppol.schemes.9929'
    | 'peppol.schemes.9930'
    | 'peppol.schemes.9914'
    | 'peppol.schemes.9945'
    | 'peppol.schemes.9910'
    | 'peppol.schemes.9925'
    | 'peppol.schemes.9944'
    | 'peppol.schemes.9947'
    | 'peppol.schemes.9949'
    | 'peppol.schemes.9934'
    | 'peppol.schemes.9926'
    | 'peppol.schemes.9931'
    | 'peppol.schemes.9937'
    | 'peppol.schemes.9939'
    | 'peppol.schemes.9935'
    | 'peppol.schemes.9920'
    | 'peppol.schemes.9946'
    | 'peppol.schemes.9957'
    | 'peppol.schemes.9932'
    | 'peppol.schemes.0211'
    | 'peppol.schemes.0208'
    | 'peppol.schemes.0192'
    | 'peppol.schemes.0184'
    | 'peppol.schemes.0007'
    | 'peppol.schemes.0088'
    | 'peppol.schemes.0060';
}

export const participantSchemes: readonly ParticipantScheme[] = [
  { code: '0245', country: 'SK', example: '2120147656', labelKey: 'peppol.schemes.0245' },
  { code: '9950', country: 'SK', preferred: true, example: 'SK2120147656', labelKey: 'peppol.schemes.9950' },
  { code: '9929', country: 'CZ', preferred: true, example: 'CZ12345678', labelKey: 'peppol.schemes.9929' },
  { code: '9930', country: 'DE', example: 'DE123456789', labelKey: 'peppol.schemes.9930' },
  { code: '9914', country: 'AT', example: 'ATU12345678', labelKey: 'peppol.schemes.9914' },
  { code: '9945', country: 'PL', example: 'PL1234567890', labelKey: 'peppol.schemes.9945' },
  { code: '9910', country: 'HU', example: 'HU12345678', labelKey: 'peppol.schemes.9910' },
  { code: '9925', country: 'BE', preferred: true, example: 'BE0123456789', labelKey: 'peppol.schemes.9925' },
  { code: '9944', country: 'NL', example: 'NL123456789B01', labelKey: 'peppol.schemes.9944' },
  { code: '9947', country: 'RO', example: 'RO12345678', labelKey: 'peppol.schemes.9947' },
  { code: '9949', country: 'SI', example: 'SI12345678', labelKey: 'peppol.schemes.9949' },
  { code: '9934', country: 'HR', example: 'HR12345678901', labelKey: 'peppol.schemes.9934' },
  { code: '9926', country: 'BG', example: 'BG123456789', labelKey: 'peppol.schemes.9926' },
  { code: '9931', country: 'EE', example: 'EE101234567', labelKey: 'peppol.schemes.9931' },
  { code: '9937', country: 'LT', example: 'LT123456789', labelKey: 'peppol.schemes.9937' },
  { code: '9939', country: 'LV', example: 'LV12345678901', labelKey: 'peppol.schemes.9939' },
  { code: '9935', country: 'IE', example: 'IE1234567T', labelKey: 'peppol.schemes.9935' },
  { code: '9920', country: 'ES', example: 'ESB12345678', labelKey: 'peppol.schemes.9920' },
  { code: '9946', country: 'PT', example: 'PT123456789', labelKey: 'peppol.schemes.9946' },
  { code: '9957', country: 'FR', example: 'FR12345678901', labelKey: 'peppol.schemes.9957' },
  { code: '9932', country: 'GB', example: 'GB123456789', labelKey: 'peppol.schemes.9932' },
  { code: '0211', example: '01234567890', labelKey: 'peppol.schemes.0211' },
  { code: '0208', example: '0123456789', labelKey: 'peppol.schemes.0208' },
  { code: '0192', example: '987654321', labelKey: 'peppol.schemes.0192' },
  { code: '0184', example: 'DK12345678', labelKey: 'peppol.schemes.0184' },
  { code: '0007', example: '1234567890', labelKey: 'peppol.schemes.0007' },
  { code: '0088', example: '5790000435968', labelKey: 'peppol.schemes.0088' },
  { code: '0060', example: '123456789', labelKey: 'peppol.schemes.0060' },
];

/**
 * The bridge's home scheme — Slovak DIČ — matching the API's own default for country SK. A bare
 * number lands here; a pasted `SK…` IČ DPH goes to 9950 instead, letters kept.
 */
export const defaultSchemeCode = '0245';

export interface ParsedParticipantInput {
  /** A recognised prefix, or null when the text decides nothing about the scheme. */
  schemeCode: string | null;
  /** What belongs in the identifier field — the part after a `scheme:` prefix, or the text as typed. */
  value: string;
}

/**
 * Reads whatever the user pasted and splits it into prefix and identifier. Three shapes are
 * understood: the directory's qualified form (`iso6523-actorid-upis::0245:2120147656`), the plain
 * `scheme:id` form, and a country-prefixed tax id such as `CZ12345678` or `SK2120147656` — the last
 * resolving through the scheme list to the country's preferred scheme, with the country letters
 * kept as part of the identifier. Anything else is left as typed, with the scheme undecided.
 */
export function parseParticipantInput(raw: string): ParsedParticipantInput {
  let text = raw.replace(/\s+/g, '');

  // The qualified form is what the directory itself returns, so it is what gets pasted back.
  text = text.replace(/^iso6523-actorid-upis::/i, '');

  const full = /^(\d{4}):(.+)$/.exec(text);
  if (full) return { schemeCode: full[1]!, value: full[2]! };

  const countryForm = /^([A-Za-z]{2})[0-9A-Za-z]+$/.exec(text);
  if (countryForm) {
    const country = countryForm[1]!.toUpperCase();
    const scheme =
      participantSchemes.find((entry) => entry.country === country && entry.preferred) ??
      participantSchemes.find((entry) => entry.country === country);

    if (scheme) return { schemeCode: scheme.code, value: text.toUpperCase() };
  }

  return { schemeCode: null, value: text };
}

/**
 * A Peppol document type identifier is unreadable in full
 * (`urn:…:Invoice-2::Invoice##urn:…billing:3.0::2.1`, optionally prefixed with
 * `busdox-docid-qns::`); the root element name sits in the `::` segment that carries the `##`
 * customisation separator, and that name is the part a person recognises. Falls back to the whole
 * string rather than guessing at shapes the directory has not shown us.
 */
export function documentTypeLabel(documentType: string): string {
  const qualified = documentType.split('::').find((segment) => segment.includes('##'));
  const local = qualified?.split('##')[0];
  return local !== undefined && local.length > 0 ? local : documentType;
}
